// Pure ordering for the Bible-search "Top" grouped preview (08.4). The five
// search lanes are otherwise independent (rule #58); this is the ONLY place
// their Top-preview order is decided, from two engine-supplied strength signals
// — no numeric score, no re-implemented matching (rule #4: the signals come
// from searchTopicsWithStrength + SearchResult.topMatchKind).
//
// Scope is deliberately minimal (O1/O3): the reference banner is pinned above
// the sections (not ordered here), Books stay first, Stories stay last, and the
// ONLY reorder is Verses↔Topics. Only present sections are emitted.

import type { TopicMatchStrength } from '../topic-search';

export type VerseTopKind =
  | 'phrase'
  | 'all-words'
  | 'word'
  | 'related' // 08.5: a related-wording verse hit; STRONG (grouped with 'phrase' → blocks a synonym topic; only a 'whole' topic beats it)
  | 'fuzzy'
  | null;

export type TopSection = 'books' | 'verses' | 'topics' | 'stories';

export function orderTopSections(s: {
  hasBooks: boolean;
  hasVerses: boolean;
  hasTopics: boolean;
  hasStories: boolean;
  /** Strength of the leading verse hit (SearchResult.topMatchKind). */
  verseTopKind: VerseTopKind;
  /** Strongest band among the live topic matches; null when no topic matched. */
  topicTopStrength: TopicMatchStrength | null;
}): TopSection[] {
  // A STRONG verse signal is a multi-word "this verse" match: an exact
  // contiguous phrase, OR an 08.5 related-wording phrase (the user remembered a
  // multi-word verse and one word differed from BSB's, e.g. "faith without
  // works" → "faith without deeds"). Both represent strong remembered-verse
  // intent, so only a 'whole' topic outranks them.
  const verseIsStrong = s.verseTopKind === 'phrase' || s.verseTopKind === 'related';

  // Topics lead Verses ONLY on strong topic intent: the query IS a curated
  // topic name or synonym term ('whole', which beats even a strong verse), OR
  // the query CONTAINS a synonym term ('synonym') and the verse side is NOT
  // strong (a lone-word 'word' or scattered 'all-words' hit does not block
  // topics; a phrase or a related phrase does — else an incidental synonym like
  // "work" inside "faith without works" would bury James 2:26).
  //
  // 'direct' DELIBERATELY does NOT lead. It fires when the query tokens appear
  // anywhere in a topic's name OR description, which is far too broad for common
  // Bible words: "god" matches the "God's Faithfulness" name, "love" matches
  // "salvation-loved-ones", "faith"/"jesus" match topic descriptions — none of
  // which is topic INTENT (the user wants the verse / the literal word). So
  // 'direct' (like 'partial'/'fuzzy'/'none') never outranks a literal verse;
  // it stays a recall/display band only. (Advisor-caught, 08.4.)
  const topicsLeadVerses =
    s.topicTopStrength === 'whole' ||
    (s.topicTopStrength === 'synonym' && !verseIsStrong);

  const order: TopSection[] = [];
  if (s.hasBooks) order.push('books'); // Books always first when present.
  if (topicsLeadVerses) {
    if (s.hasTopics) order.push('topics');
    if (s.hasVerses) order.push('verses');
  } else {
    if (s.hasVerses) order.push('verses');
    if (s.hasTopics) order.push('topics');
  }
  if (s.hasStories) order.push('stories'); // Stories always last when present.
  return order;
}
